import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { formatAmerican, upsetPrice } from '../../../utils/mlb-the-show/odds.js';
import { ovrOf, rankPlayers } from '../../../utils/mlb-the-show/rating.js';
import { shortDate, signed } from '../../../utils/mlb-the-show/format.js';
import { spring, timing } from '../motion.js';
import { Empty, useShake } from './Feedback.js';
import { Ovr } from './primitives.js';

const deltaFor = (league, game, id) => {
  const d = game ? league.perGame[game.id] : undefined;
  if (!d) return 0;
  if (game.home === id) return d.homeDelta;
  if (game.away === id) return d.awayDelta;
  return 0;
};

export function Leaderboard({ players, league, lastGame }) {
  const rows = useMemo(() => rankPlayers(players, league), [players, league]);
  const order = rows.map((r) => r.player.id).join(',');
  const prevOrder = useRef(null);
  const [moves, setMoves] = useState({});

  useEffect(() => {
    const now = order.split(',');
    const before = prevOrder.current;
    prevOrder.current = now;
    if (!before) return undefined;
    const diff = {};
    now.forEach((id, i) => {
      const j = before.indexOf(id);
      if (j >= 0 && j !== i) diff[id] = j - i;
    });
    if (!Object.keys(diff).length) return undefined;
    setMoves(diff);
    const t = setTimeout(() => setMoves({}), timing.rankMoveMs);
    return () => clearTimeout(t);
  }, [order]);

  if (!rows.length) return <Empty>No players yet. Add some on the Players tab.</Empty>;

  return (
    <div className="st-pane">
      {rows.map((r, i) => {
        const id = r.player.id;
        const inLast = Boolean(lastGame) && (lastGame.home === id || lastGame.away === id);
        const delta = deltaFor(league, lastGame, id);
        const mv = moves[id];
        const form = r.record.form.slice(-5);
        return (
          <m.div key={id} layout className={`st-lrow${inLast ? ' st-lrow--active' : ''}`} transition={spring.standard}>
            <span className={`st-rank${i === 0 ? ' st-rank--top' : ''}`}>{i + 1}</span>
            <span className="st-av">{r.player.name[0]?.toUpperCase()}</span>
            <div className="st-who">
              <div className="st-pn">
                <span>{r.player.name}</span>
                <AnimatePresence>
                  {mv && (
                    <m.span
                      key={`mv${mv}`}
                      className={`st-move ${mv > 0 ? 'st-move--up' : 'st-move--down'}`}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      {mv > 0 ? '▲' : '▼'}
                      {Math.abs(mv)}
                    </m.span>
                  )}
                </AnimatePresence>
              </div>
              <div className="st-pr">
                {r.record.wins}–{r.record.losses}
              </div>
            </div>
            <div className="st-form" aria-label={`Last games: ${form.map((f) => (f === 'w' ? 'win' : 'loss')).join(', ') || 'none'}`}>
              {form.map((f, j) => (
                <i key={j} className={f === 'w' ? 'st-form-w' : 'st-form-l'} />
              ))}
            </div>
            <Ovr value={r.ovr} delta={delta} deltaKey={delta && lastGame ? lastGame.id : null} />
          </m.div>
        );
      })}
    </div>
  );
}

export function History({ players, games, league, standardInnings, highlightId, onDelete, pageSize = 12 }) {
  const [shown, setShown] = useState(pageSize);
  const names = useMemo(() => new Map(players.map((p) => [p.id, p.name])), [players]);
  const all = useMemo(
    () =>
      games
        .filter((game) => names.has(game.home) && names.has(game.away))
        .sort((a, b) => b.createdAt - a.createdAt),
    [games, names],
  );
  const list = all.slice(0, shown);

  return (
    <div className="st-pane">
      <AnimatePresence initial={false}>
        {list.map((game) => {
          const homeWon = game.homeScore > game.awayScore;
          const d = league.perGame[game.id];
          const winnerDelta = d ? (homeWon ? d.homeDelta : d.awayDelta) : 0;
          const upset = d ? upsetPrice(d.homeWinChance, homeWon) : null;
          const awayName = names.get(game.away);
          const homeName = names.get(game.home);
          return (
            <m.div
              key={game.id}
              layout
              className="st-hrow"
              transition={spring.standard}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -30, transition: { duration: 0.18 } }}
            >
              {highlightId === game.id && (
                <m.i
                  className="st-flash"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: 1.4, delay: 0.3 }}
                />
              )}
              <span className="st-hd">{shortDate(game.date)}</span>
              <span className="st-hm">
                {homeWon ? awayName : <b>{awayName}</b>}
                <span className="st-sc">
                  {game.awayScore}–{game.homeScore}
                </span>
                {homeWon ? <b>{homeName}</b> : homeName}
                {game.innings !== standardInnings && <span className="st-xi">F/{game.innings}</span>}
                {upset && <span className="st-upset">Upset {formatAmerican(upset)}</span>}
              </span>
              <span className="st-hdl">{winnerDelta ? signed(winnerDelta) : ''}</span>
              <button
                type="button"
                className="st-hx"
                aria-label={`Delete ${awayName} ${game.awayScore}–${game.homeScore} ${homeName} from ${shortDate(game.date)}`}
                onClick={() => onDelete(game)}
              >
                ×
              </button>
            </m.div>
          );
        })}
      </AnimatePresence>
      {all.length > shown && (
        <button type="button" className="st-more" onClick={() => setShown((n) => n + pageSize)}>
          Show {Math.min(pageSize, all.length - shown)} older games · {all.length - shown} left
        </button>
      )}
      {all.length === 0 && <Empty>No games yet. Save one above.</Empty>}
    </div>
  );
}

export function Roster({ players, league, onAdd, onRemove }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [controls, runShake] = useShake();

  const submit = (e) => {
    e.preventDefault();
    const n = name.trim();
    const problem = !n
      ? 'Type a name first'
      : players.some((p) => p.name.toLowerCase() === n.toLowerCase())
        ? `${n} is already on the roster`
        : '';
    if (problem) {
      setError(problem);
      runShake();
      return;
    }
    onAdd(n);
    setName('');
    setError('');
  };

  return (
    <div className="st-pane">
      <m.form className="st-addf" animate={controls} onSubmit={submit}>
        <input
          aria-label="Player name"
          placeholder="Add a player, e.g. Marcus"
          value={name}
          maxLength={16}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError('');
          }}
        />
        <button className="st-addbtn" type="submit">
          Add
        </button>
      </m.form>
      <div className="st-addhint" aria-live="polite">
        {error}
      </div>
      <div className="st-roster">
        <AnimatePresence initial={false} mode="popLayout">
          {players.map((p) => {
            const rec = league.record[p.id] ?? { wins: 0, losses: 0 };
            return (
              <m.div
                key={p.id}
                layout
                className="st-prow"
                transition={spring.standard}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
              >
                <span className="st-av">{p.name[0]?.toUpperCase()}</span>
                <div className="st-who">
                  <div className="st-pn">{p.name}</div>
                  <div className="st-pr">
                    {rec.wins}–{rec.losses}
                  </div>
                </div>
                <Ovr value={ovrOf(league, p.id)} />
                <button type="button" className="st-hx" aria-label={`Remove ${p.name}`} onClick={() => onRemove(p)}>
                  ×
                </button>
              </m.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
